'use client';

/**
 * Process-lifetime REACTIVE store of a post's engagement stats (viewer `liked` +
 * like / comment counts), keyed by `storeId:postId`. The web analogue of the
 * shared `postCache` in `useFetchPostById` — every surface showing the same post
 * (a feed card's footer, the detail modal's footer, the comments-panel header)
 * reads ONE entry, so a like or a new comment reflects everywhere at once instead
 * of drifting per instance.
 *
 * Two writer classes with a deliberate precedence:
 *   - SERVER fetches ({@link applyFetchedCounts} / {@link applyFetchedLiked}) seed
 *     the initial values from the (eventually-consistent) search index.
 *   - LOCAL optimistic writes ({@link patchLocalStats} / {@link bumpCommentCount})
 *     — the viewer's own like / comment — mark the entry {@link localOverride}.
 *     Once set, later server fetches are IGNORED for that post: the index lags a
 *     write, so re-reading it would flip a just-liked heart back to 0. The
 *     viewer's in-session action is the truth until a reload.
 */
export interface PostStats {
  liked: boolean;
  likeCount: number;
  commentCount: number;
}

const store = new Map<string, PostStats>();
const localOverride = new Set<string>();
const subscribers = new Map<string, Set<() => void>>();

export function postStatsKey(storeId: string, postId: string): string {
  return `${storeId}:${postId}`;
}

function notify(key: string): void {
  const set = subscribers.get(key);
  if (!set) return;
  for (const listener of Array.from(set)) {
    listener();
  }
}

export function getPostStats(key: string): PostStats | undefined {
  return store.get(key);
}

export function subscribePostStats(
  key: string,
  listener: () => void,
): () => void {
  let set = subscribers.get(key);
  if (!set) {
    set = new Set();
    subscribers.set(key, set);
  }
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

/**
 * Create the entry from a snapshot if it doesn't exist yet; a no-op once present
 * (a re-mount, or a second surface for the same post, must not reset a value a
 * sibling already advanced). No notify — the first mount has no listeners whose
 * render depends on it. The FIRST surface to render for a post owns the seed; in
 * the detail view that's always the footer (rendered before the comments panel),
 * so `liked` / `likeCount` come from the real post snapshot, not a panel default.
 */
export function ensurePostStats(key: string, initial: PostStats): void {
  if (!store.has(key)) store.set(key, initial);
}

/** Apply server-fetched public counts — skipped once the viewer has locally mutated this post. */
export function applyFetchedCounts(
  key: string,
  counts: { likeCount: number; commentCount: number },
): void {
  if (localOverride.has(key)) return;
  const prev = store.get(key);
  if (!prev) return;
  store.set(key, { ...prev, ...counts });
  notify(key);
}

/** Apply the viewer's fetched `liked` — skipped once they've toggled locally. */
export function applyFetchedLiked(key: string, liked: boolean): void {
  if (localOverride.has(key)) return;
  const prev = store.get(key);
  if (!prev || prev.liked === liked) return;
  store.set(key, { ...prev, liked });
  notify(key);
}

/** A local optimistic write (the viewer's own like) — outranks later server fetches. */
export function patchLocalStats(
  key: string,
  partial: Partial<PostStats>,
): void {
  const prev = store.get(key);
  if (!prev) return;
  localOverride.add(key);
  store.set(key, { ...prev, ...partial });
  notify(key);
}

/** Optimistically move the comment count (the viewer's own create) and lock out server clobbers. */
export function bumpCommentCount(key: string, delta: number): void {
  const prev = store.get(key);
  if (!prev) return;
  localOverride.add(key);
  store.set(key, {
    ...prev,
    commentCount: Math.max(0, prev.commentCount + delta),
  });
  notify(key);
}
